import {
  BuildingIcon,
  LogOutIcon,
  MailIcon,
  PencilEdit02Icon,
  TelephoneIcon,
} from '@/icons';
import { Dropdown, Flex } from 'antd';
import { Link, useNavigate } from 'react-router';
import { useAppDispatch } from '../../app/app.store';
import {
  useGetMyOrganizationsQuery,
  useLogoutSessionMutation,
} from '../../app/features/auth/auth.api';
import { userLoggedOut } from '../../app/features/auth/auth.slice';
import IMAGES from '../../assets/images';
import CustomImage from '../../components/CustomImage';
import useAuth from '../../hooks/useAuth';
const ProfileMenu = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { profileInfo, sessionId, selectedOrganization } = useAuth();
  const [logoutSession] = useLogoutSessionMutation();

  // Cached by boot; only multi-organization logins have a session.
  const { data: myOrganizations } = useGetMyOrganizationsQuery(undefined, {
    skip: !sessionId,
  });
  const canSwitch = (myOrganizations?.data?.organizations?.length ?? 0) > 1;

  const handleLogout = async () => {
    if (sessionId) {
      // Best effort: log out locally even if the session cannot be revoked.
      await logoutSession()
        .unwrap()
        .catch(() => undefined);
    }
    dispatch(userLoggedOut());
  };

  const dropdownContent = (
    <div className="w-[370px] bg-theme-slate-900 rounded-xl shadow-2xl border border-theme-gray-700 overflow-hidden px-6 py-8 pb-6">
      {/* Profile Section */}
      <div>
        <Flex
          align="center"
          gap={24}
          className="mb-4 border-b border-theme-gray-700 pb-4"
        >
          <CustomImage
            size={56}
            className="rounded-full object-cover"
            src={profileInfo?.photo || IMAGES.avatar}
          />
          <div className="flex-1">
            <Flex align="center" gap={8}>
              <h3 className="dark:text-white text-2xl font-bol">
                {profileInfo?.name}
              </h3>
              <Link
                to={`/admin/employee/dashboard/employee-details/${profileInfo?.id}/manage`}
                className="flex items-center"
              >
                <button className="text-theme-primary text-xl">
                  <PencilEdit02Icon size={20} />
                </button>
              </Link>
            </Flex>
            <p className="dark:text-white text-lg font-medium">
              {profileInfo?.designation?.name}
            </p>
          </div>
        </Flex>

        {/* Contact Info */}
        <div className="space-y-3 mb-5">
          <Flex align="center" gap={16} className="dark:text-white text-lg">
            <MailIcon />
            <span>{profileInfo?.email || 'N/A'}</span>
          </Flex>
          <Flex align="center" gap={16} className="dark:text-white text-lg">
            <TelephoneIcon />
            <span>{profileInfo?.phone || 'N/A'}</span>
          </Flex>
        </div>
      </div>

      {/* Company (multi-organization logins only) */}
      {selectedOrganization && (
        <Flex
          align="center"
          justify="space-between"
          gap={12}
          className="mb-5 p-3 rounded-xl border border-theme-gray-700"
        >
          <Flex align="center" gap={12} className="min-w-0 dark:text-white">
            <BuildingIcon />
            <span className="truncate text-lg">
              {selectedOrganization.name}
            </span>
          </Flex>
          {canSwitch && (
            <button
              onClick={() => navigate('/select-organization')}
              className="shrink-0 text-theme-primary text-sm font-medium"
            >
              Switch company
            </button>
          )}
        </Flex>
      )}

      {/* Logout Button */}
      <button
        onClick={handleLogout}
        className="w-full flex items-center justify-center gap-2 px-4 py-1.5 border-2 border-theme-gray-700 hover:bg-theme-gray-700 dark:text-white rounded-xl text-sm font-medium transition-colors"
      >
        <Flex
          align="center"
          justify="center"
          className="size-[30px] rounded-full bg-theme-gray-700"
        >
          <LogOutIcon size={15} />
        </Flex>
        <span>Log out</span>
      </button>
    </div>
  );

  return (
    <Dropdown
      placement="bottomRight"
      arrow={{ pointAtCenter: true }}
      className="cursor-pointer"
      rootClassName="!left-auto !right-5 profile-dropdown"
      popupRender={() => dropdownContent}
      trigger={['click']}
    >
      <div className="flex items-center gap-2.5 cursor-pointer">
        <CustomImage
          size={44}
          className="rounded-full object-cover"
          src={profileInfo?.photo || IMAGES.avatar}
        />
      </div>
    </Dropdown>
  );
};

export default ProfileMenu;
